import { useCallback, useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import type { HomeUser } from '../homeTypes';

interface GithubUsernameResponse {
  found?: boolean;
  username?: string;
  message?: string;
  remainingSeconds?: number;
}

export function useHomeIdentity(backendUrl: string): {
  homeUser: HomeUser;
  githubSyncInProgress: boolean;
  refreshIdentity: () => Promise<HomeUser>;
} {
  const identityApi = useApi(identityApiRef);
  const [homeUser, setHomeUser] = useState<HomeUser>({});
  const [githubSyncInProgress, setGithubSyncInProgress] = useState(false);

  const lookupGithubUsername = useCallback(
    async (email: string) => {
      try {
        const response = await fetch(
          `${backendUrl}/api/github/username-from-email?email=${encodeURIComponent(
            email,
          )}`,
        );
        if (!response.ok) return undefined;
        const data: GithubUsernameResponse = await response.json();
        if (data.found && typeof data.username === 'string') {
          setGithubSyncInProgress(false);
          return data.username;
        }
        // Check if sync is in progress or was triggered
        const message = typeof data.message === 'string' ? data.message : '';
        setGithubSyncInProgress(
          message.includes('already in progress') ||
            !(message.includes('wait') && data.remainingSeconds),
        );
      } catch (error) {
        console.error('Error looking up GitHub username:', error);
      }
      return undefined;
    },
    [backendUrl],
  );

  const refreshIdentity = useCallback(async (): Promise<HomeUser> => {
    try {
      // Get GitHub username from email using SSO mapping
      // Microsoft SSO provides email, we use it to lookup GitHub username
      const { email } = await identityApi.getProfileInfo();
      const githubUsername = email
        ? await lookupGithubUsername(email)
        : undefined;

      const user = { email, githubUsername };
      setHomeUser(user);
      return user;
    } catch (error) {
      console.error('Error fetching user identity:', error);
      setHomeUser({});
      return {};
    }
  }, [identityApi, lookupGithubUsername]);

  useEffect(() => {
    void refreshIdentity();
  }, [refreshIdentity]);

  return { homeUser, githubSyncInProgress, refreshIdentity };
}
